import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const SoundContext = createContext({ enabled: false, toggle: () => {}, playWhoosh: () => {} });

const STORAGE_KEY = "unite-sound-enabled";

// Synthesizes a soft brushstroke "whoosh" using WebAudio (filtered noise + envelope + sweep).
// No external audio file — keeps the bundle lean and avoids hosting costs.
function makeWhoosh(ctx) {
  const duration = 0.55;
  const bufferSize = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  // pink-ish noise (low-passed white noise) for softness
  let lastVal = 0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    lastVal = (lastVal + 0.04 * white) / 1.04;
    data[i] = lastVal * 4;
  }
  return buffer;
}

export function SoundProvider({ children }) {
  const [enabled, setEnabled] = useState(false);
  const ctxRef = useRef(null);
  const bufferRef = useRef(null);

  // Load preference from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "true") setEnabled(true);
    } catch {
      /* ignore */
    }
  }, []);

  const ensureContext = useCallback(() => {
    if (!ctxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      ctxRef.current = new AudioCtx();
      bufferRef.current = makeWhoosh(ctxRef.current);
    }
    // Resume context if it was suspended (browsers auto-suspend without user gesture)
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume().catch(() => {});
    }
    return ctxRef.current;
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      if (next) {
        // Initialise context on user gesture so future plays work
        const ctx = ensureContext();
        if (ctx) {
          // play a tiny confirmation tick
          playWhooshInternal(ctx, bufferRef.current, 0.08, 0.35);
        }
      }
      return next;
    });
  }, [ensureContext]);

  const playWhoosh = useCallback(() => {
    if (!enabled) return;
    const ctx = ensureContext();
    if (!ctx || !bufferRef.current) return;
    playWhooshInternal(ctx, bufferRef.current, 0.18, 0.55);
  }, [enabled, ensureContext]);

  return (
    <SoundContext.Provider value={{ enabled, toggle, playWhoosh }}>
      {children}
    </SoundContext.Provider>
  );
}

function playWhooshInternal(ctx, buffer, peakGain, duration) {
  const now = ctx.currentTime;
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  // Band-pass filter that sweeps to create the brushstroke "whoosh"
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = 1.2;
  filter.frequency.setValueAtTime(400, now);
  filter.frequency.exponentialRampToValueAtTime(2800, now + duration * 0.6);
  filter.frequency.exponentialRampToValueAtTime(900, now + duration);

  // Envelope
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peakGain, now + 0.06);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  source.start(now);
  source.stop(now + duration + 0.05);
}

export function useSound() {
  return useContext(SoundContext);
}
